import { type ZonedDateTime } from '@internationalized/date';
import {
    createProperties,
    createRoot,
    type Field,
    Form,
    type FormState,
    type TypeToFields,
} from '@jmvdevelop/svelte-form';
import findIndex from 'lodash/findIndex';
import type { FeedPostTagEnum, ParticipantInput } from '$lib/gql/graphql';
import { futureZonedDateTime } from '$lib/validator/date';
import { notBlankAndNotNull, notNull } from '$lib/validator/validator.js';

export type FeedPostFormData = {
    author: ParticipantInput;
    tag: FeedPostTagEnum | null;
    body: string;
    medias: number[];
    linkMetaDatas: number[];
    startDate: ZonedDateTime | null;
    endDate: ZonedDateTime | null;
    pendingPublicationDate: ZonedDateTime | null;
    withPendingPublicationDate: boolean;
    relatedCategory: number | null;
    country: string | null;
};

export type Fields<TFormValues> = TypeToFields<TFormValues, FeedPostFormData>;

export function createFields<TValues>(
    self: Field<TValues, FeedPostFormData>,
): Fields<TValues> {
    return {
        self: self,
        ...createProperties(self, [
            'author',
            'body',
            'tag',
            'medias',
            'linkMetaDatas',
            'startDate',
            'endDate',
            'pendingPublicationDate',
            'withPendingPublicationDate',
            'relatedCategory',
            'country',
        ] as const),
    };
}

export function validate(
    form: Form<FeedPostFormData, string>,
    fields: Fields<FeedPostFormData>,
    state: FormState<FeedPostFormData, string>,
    sharedPostId: number | null,
) {
    const values = state.values;
    if (sharedPostId == null) {
        state = notBlankAndNotNull(values.body, {
            form,
            field: fields.body,
            message: 'Vous devez saisir du texte comme contenu du post',
        })(state);
    }

    if (values.withPendingPublicationDate) {
        state = notNull(values.pendingPublicationDate?.toString(), {
            form,
            field: fields.pendingPublicationDate,
            message: 'Veuillez choisir la date et heure de publication',
        })(state);

        state = futureZonedDateTime(values.pendingPublicationDate, {
            form,
            field: fields.pendingPublicationDate,
            message: 'Veuillez choisir une date et heure dans le future',
        })(state);
    }
    return state;
}

export function createForm(
    id: string,
    initialValues: FeedPostFormData,
    sharedPostId: number | null,
): [Form<FeedPostFormData, string>, Fields<FeedPostFormData>] {
    const fields = createFields<FeedPostFormData>(createRoot<FeedPostFormData>());
    const form = new Form<FeedPostFormData, string>({ initialValues: initialValues }, id);

    form.addValidator((state) => {
        return validate(form, fields, state, sharedPostId);
    });

    // If withPendingPublicationDate changes from true to false, then pendingPublicationDate is set to null
    //
    form.addOnChangeValues((changedFields, prevState, nextState) => {
        if (
            findIndex(
                changedFields,
                (f) => f.getKey() === fields.withPendingPublicationDate.getKey(),
            ) !== -1 &&
            findIndex(
                changedFields,
                (f) => f.getKey() === fields.pendingPublicationDate.getKey(),
            ) === -1
        ) {
            const prevValue = fields.withPendingPublicationDate.getValue(
                prevState.values,
            );
            const nextValue = fields.withPendingPublicationDate.getValue(
                nextState.values,
            );

            if (prevValue && !nextValue) {
                nextState = form
                    .actions()
                    .changeValue(null, fields.pendingPublicationDate)(nextState);
            }
        }

        return nextState;
    });

    return [form, fields];
}
